import { CaretDown } from "@phosphor-icons/react";
import { forwardRef, useId, useState, type HTMLAttributes, type ReactNode } from "react";
import styled from "@emotion/styled";
import { twRing, twTransform } from "@/lib/emotionTw";

export type CollapsibleProps = Omit<HTMLAttributes<HTMLDivElement>, "title"> & {
  title: ReactNode;
  description?: ReactNode;
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
};

/* Exact compiled output of the pre-migration Tailwind utilities. */

const CollapsibleRoot = styled("div", {
  label: "AgenticCollapsible-root"
})({
  borderRadius: "var(--bfrs-radius-xl)",
  borderWidth: "1px",
  "--tw-border-opacity": "1",
  borderColor: "hsl(var(--bfrs-color-border) / var(--tw-border-opacity, 1))",
  "--tw-bg-opacity": "1",
  backgroundColor: "hsl(var(--bfrs-color-surface-card) / var(--tw-bg-opacity, 1))"
});

const CollapsibleTrigger = styled("button", {
  label: "AgenticCollapsible-trigger"
})({
  display: "flex",
  width: "100%",
  alignItems: "center",
  gap: ".75rem",
  paddingLeft: "1rem",
  paddingRight: "1rem",
  paddingTop: ".75rem",
  paddingBottom: ".75rem",
  textAlign: "left",
  outline: "2px solid transparent",
  outlineOffset: "2px",
  "&:focus-visible": twRing("2px", "hsl(var(--bfrs-color-border-focus) / .25)")
});

const CollapsibleHeaderText = styled("span", {
  label: "AgenticCollapsible-headerText"
})({
  minWidth: "0px",
  flex: "1 1 0%"
});

const CollapsibleTitle = styled("span", {
  label: "AgenticCollapsible-title"
})({
  display: "block",
  fontSize: ".875rem",
  lineHeight: "1.25rem",
  fontWeight: 600,
  "--tw-text-opacity": "1",
  color: "hsl(var(--bfrs-color-text-primary) / var(--tw-text-opacity, 1))"
});

const CollapsibleDescription = styled("span", {
  label: "AgenticCollapsible-description"
})({
  marginTop: ".125rem",
  display: "block",
  fontSize: ".75rem",
  lineHeight: "1.25rem",
  "--tw-text-opacity": "1",
  color: "hsl(var(--bfrs-color-text-secondary) / var(--tw-text-opacity, 1))"
});

const CollapsibleCaret = styled(CaretDown, {
  label: "AgenticCollapsible-caret",
  shouldForwardProp: (propName) => propName !== "ownerState"
})<{ ownerState: { open: boolean } }>(({ ownerState }) => [
  {
    flexShrink: 0,
    "--tw-text-opacity": "1",
    color: "hsl(var(--bfrs-color-text-muted) / var(--tw-text-opacity, 1))",
    transitionProperty: "transform",
    transitionTimingFunction: "cubic-bezier(.4,0,.2,1)",
    transitionDuration: ".15s"
  },
  ownerState.open && twTransform({ "--tw-rotate": "180deg" })
]);

const CollapsiblePanel = styled("div", {
  label: "AgenticCollapsible-panel"
})({
  borderTopWidth: "1px",
  "--tw-border-opacity": "1",
  borderColor: "hsl(var(--bfrs-color-border) / var(--tw-border-opacity, 1))",
  paddingLeft: "1rem",
  paddingRight: "1rem",
  paddingTop: ".75rem",
  paddingBottom: ".75rem"
});

export const Collapsible = forwardRef<HTMLDivElement, CollapsibleProps>(({ className, title, description, open, defaultOpen, onOpenChange, children, ...props }, ref) => {
  const [internalOpen, setInternalOpen] = useState(Boolean(defaultOpen));
  const panelId = useId();
  const isOpen = open ?? internalOpen;
  const setOpen = (nextOpen: boolean) => {
    if (open === undefined) setInternalOpen(nextOpen);
    onOpenChange?.(nextOpen);
  };

  return (
    <CollapsibleRoot ref={ref} className={className} {...props}>
      <CollapsibleTrigger type="button" aria-expanded={isOpen} aria-controls={panelId} onClick={() => setOpen(!isOpen)}>
        <CollapsibleHeaderText>
          <CollapsibleTitle>{title}</CollapsibleTitle>
          {description && <CollapsibleDescription>{description}</CollapsibleDescription>}
        </CollapsibleHeaderText>
        <CollapsibleCaret size={16} weight="bold" ownerState={{ open: isOpen }} />
      </CollapsibleTrigger>
      {isOpen && (
        <CollapsiblePanel id={panelId}>
          {children}
        </CollapsiblePanel>
      )}
    </CollapsibleRoot>
  );
});

Collapsible.displayName = "Collapsible";
